import React, { useState, useEffect, useContext } from "react";

import classNames from "classnames";
import { ToastContainer, toast } from "react-toastify";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import Markdown from "../../styles/Markdown";

const notify = () => toast("Copied URL to clipboard!");
const Container = styled.div`
  background-color: ${(props) => props.backgroundColour || "#fff"};
  padding: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  ${(props) => props.dynamicStyles || ""}
`;

const Label = styled.label`
  display: block;
  margin-top: 10px;
  align-self: flex-start;

  &:after {
    content: "*";
    color: red;
    margin-left: 5px;
  }
`;

const Input = styled.input`
  margin-bottom: 10px;
  padding: 10px;
  width: 100%;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const Button = styled.button`
  padding: 10px 20px;
  width: 100%;
  max-width: 200px;
  margin: auto;
  background-color: ${({ theme }) => `${theme.primaryColor}`};
  color: ${({ theme }) => theme.negativeTextColor};
  border: none;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border-radius: 5px;

  &:hover {
    background-color: ${(props) =>
      props.disabled ? "#007bff" : props.theme.secondaryColor};
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const LinkDisplay = styled.p`
  margin: 10px 0;
  padding: 10px;
`;

const CopyButton = styled(Button)`
  background-color: ${({ theme }) => `${theme.primaryColor}`};
  color: ${({ theme }) => theme.negativeTextColor};
  border-radius: 5px;
  &:hover {
    background-color: ${(props) =>
      props.disabled ? "#007bff" : props.theme.secondaryColor};
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

function ReferAFriend() {
  const {
    backgroundColour = "#fff",
    text = "Refer a friend",
    buttonText = "Create Link",
    includeEmailAddress = false,
    includeFirstName = true,
    includeLastName = true,
    fullWidth = false,
    componentClassName = "",
    CSS = "",
    successText = "Your link is:",
    copyButtonText = "Copy",
    customSiteUrl = "",
  } = useContext(ComponentContext);

  const rowClassName = classNames(
    { row: !fullWidth },
    componentClassName,
    "refer-a-friend"
  );

  const [link, setLink] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);

  useEffect(() => {
    setIsButtonEnabled(
      (includeFirstName ? firstName.trim() !== "" : true) &&
        (includeLastName ? lastName.trim() !== "" : true) &&
        (includeEmailAddress
          ? email.trim() !== "" && email.includes("@")
          : true)
    );
  }, [
    firstName,
    lastName,
    email,
    includeFirstName,
    includeLastName,
    includeEmailAddress,
  ]);

  const createLink = () => {
    const baseDomain = window.location.origin;
    const campaignCode = encodeURIComponent(firstName + lastName + email);
    const siteUrl = customSiteUrl || baseDomain;
    const newLink = `${siteUrl}/?utm_source=referafriend&utm_campaign=${campaignCode}`;
    setLink(newLink);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(link).then(() => {
      notify();
    });
  };

  return (
    <Container
      backgroundColour={backgroundColour}
      dynamicStyles={CSS}
      className={rowClassName}
      id="refer-a-friend"
    >
      <ToastContainer />
      {link ? (
        <>
          <Markdown>{successText}</Markdown>
          <LinkDisplay backgroundColour={backgroundColour}>{link}</LinkDisplay>
          <CopyButton
            backgroundColour={backgroundColour}
            onClick={copyToClipboard}
          >
            {copyButtonText}
          </CopyButton>
        </>
      ) : (
        <>
          <Markdown>{text}</Markdown>
          <div className="mb-3 d-flex justify-content-center w-100">
            {includeFirstName && (
              <div className="d-flex flex-column align-items-start mx-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First Name"
                />
              </div>
            )}
            {includeLastName && (
              <div className="d-flex flex-column align-items-start mx-2">
                <Label htmlFor="lastName">Surname</Label>
                <Input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Surname"
                />
              </div>
            )}
            {includeEmailAddress && (
              <div className="d-flex flex-column align-items-start mx-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email Address"
                />
              </div>
            )}
          </div>
          <Button onClick={createLink} disabled={!isButtonEnabled}>
            {buttonText}
          </Button>
        </>
      )}
    </Container>
  );
}

export default ReferAFriend;
